import getRemoteMetricKey from 'common/src/modules/analytics/utils/getRemoteMetricKey';
import {AvailableDimension} from 'common/src/modules/campaigns/store/insightsTab/insightsTab.types';
import dashboardsApi from 'common/src/modules/dashboards/dashboards.api';
import {defaultWidgetSizes} from 'common/src/modules/dashboards/dashboards.constants';
import {
  AiDashboardWidgetType,
  DashboardWidget,
  DashboardWidgetType,
} from 'common/src/modules/dashboards/dashboards.types';
import {
  refreshAccountDashboards,
  refreshDashboardWidgets,
} from 'common/src/modules/dashboards/store/features';
import getNewItemPosition from 'common/src/modules/dashboards/utils/getNewItemPosition';
import widgetNameByMetricsAndDimensions from 'common/src/modules/dashboards/utils/widgetNameByMetricsAndDimensions';
import {ToolExecuteParams, ToolResult} from 'common/src/modules/shared/copilot/copilot.types';
import tool from 'common/src/modules/shared/copilot/utils/creators/tool';
import toolResult from 'common/src/modules/shared/copilot/utils/creators/toolResult';
import toolError from 'common/src/modules/shared/copilot/utils/toolError';
import {CLStore} from 'common/src/redux/store';
import getMatchingEnumValue from 'common/src/utils/typescriptUtils/getMatchingEnumValue';
import _map from 'lodash/map';
import {
  AiAnalyticsEventType,
  AnalyticsEventType,
  DimensionValue,
  MetricValue,
} from '../../../../analytics.types';
import getRemoteDimensionKey from '../../../../utils/getRemoteDimensionKey';
import {copilotAnalyticsWidgetSchema} from '../../../copilotAnalytics.schemas';
import {AccountAnalyticsToolName} from '../accountAnalyticsAgent.enums';
import partitionByEventType from '../utils/partitionByEventType';
import {FeatureFlagKey} from 'common/src/modules/featureFlags/featureFlags.types';
import isFlagEnabled from 'common/src/modules/featureFlags/utils/isFlagEnabled';

function createWidgetTool(store: CLStore) {
  async function execute({
    input: {
      dashboardId,
      dimension: aiDimension,
      metrics: aiMetrics,
      breakdownByTime,
      type,
      eventType,
      withTrends,
    },
  }: ToolExecuteParams<typeof copilotAnalyticsWidgetSchema>): Promise<ToolResult> {
    const {
      dashboardPage: {metricsAndDimensionsOptions},
    } = store.getState();

    if (!dashboardId) {
      return toolError('Dashboard ID is required to create a widget.');
    }

    const selectedEventType = getMatchingEnumValue(
      AiAnalyticsEventType,
      AnalyticsEventType,
      eventType,
    );

    const [dimensionsByEventType] = partitionByEventType(
      metricsAndDimensionsOptions.dimensions,
      selectedEventType,
    ) as unknown as AvailableDimension[][];

    const dimension =
      getRemoteDimensionKey<DimensionValue>(dimensionsByEventType, aiDimension) ??
      DimensionValue.Date;

    const metrics = _map(aiMetrics, (metric) =>
      getRemoteMetricKey(metricsAndDimensionsOptions.metrics, metric),
    ) as MetricValue[];

    const widgetType = getMatchingEnumValue(
      AiDashboardWidgetType,
      DashboardWidgetType,
      type,
    ) as DashboardWidgetType;

    const dashboardWidgets = await dashboardsApi.getWidgets(dashboardId);

    const prevWidgets = dashboardWidgets || [];

    const widgetWidth = defaultWidgetSizes[widgetType].width;
    const widgetHeight = defaultWidgetSizes[widgetType].height;

    const {x, y} = getNewItemPosition(prevWidgets, widgetWidth);

    const widget: DashboardWidget = {
      id: 0,
      dashboardId,
      filters: [],
      name: widgetNameByMetricsAndDimensions(
        metrics,
        [dimension as DimensionValue],
        widgetType as DashboardWidgetType,
      ),
      breakdownByTime: breakdownByTime,
      type: widgetType,
      dimensions: [dimension as DimensionValue],
      metrics,
      config: [],
      layout: {
        i: String(0),
        x: x,
        y: y,
        w: widgetWidth,
        h: widgetHeight,
      },
      children: [],
      showTrends: withTrends ?? false,
      ...(selectedEventType
        ? {eventType: selectedEventType}
        : {eventType: AnalyticsEventType.Adserving}),
    };

    try {
      const widgetId = await dashboardsApi.createWidget({...widget, dashboardId});

      await store.dispatch(refreshAccountDashboards());
      await store.dispatch(refreshDashboardWidgets({dashboardId}));

      const isSkillsMode = isFlagEnabled(FeatureFlagKey.EnableCopilotSkills);

      if (isSkillsMode) {
        return toolResult(String(widgetId));
      }

      return toolResult(`Created widget with id: ${widgetId}`);
    } catch (error) {
      return toolError(
        `Failed to create widget: ${error instanceof Error ? error.message : 'Unknown error'}`,
      );
    }
  }

  return tool({
    name: AccountAnalyticsToolName.CreateAccountWidget,
    displayName: 'Creating widget',
    description: 'create a widget for account dashboard',
    execute,
    inputSchema: copilotAnalyticsWidgetSchema,
  });
}

export default createWidgetTool;
